import React, { useEffect, useState } from 'react';
import { loadCountryZones, decideZone } from '../../utils/timezones';

// The time zone of a company, client location, district, district manager or guard, worked out rather than
// picked (utils/timezones): read-only text when there is one answer, a list of just the country's zones
// when it has several. onChange(zone) is called whenever the zone is worked out or chosen (null: none yet).
// latitude / longitude: only for a client location, whose zone comes from its map position.
// loadZones(country): where the country's zones come from (the public sign-up page has no admin token).
// onlyWhenChoice (the sign-up page): nothing at all is shown while there is no country or the country has one zone
// (set without a word, as always); only a country with several zones shows a "Time zone" list to choose from.
// onZones(zones): the country's zones once known (the sign-up page then knows whether a choice is needed).
// row (the admin forms): the same, as a form row "Time zone" with the list; nothing at all (no line) while there is
// no country, the country has one zone, or a client location's map pin decides it (all set without a word).
let rowIds = 0;

export default function TimeZoneField({ country, value, onChange, latitude, longitude, loadZones, onlyWhenChoice, onZones, row }) {
	const [rowId] = useState(() => 'time-zone-row-' + (++rowIds));
	const [zones, setZones] = useState(null);

	useEffect(() => {
		let current = true;
		setZones(null);
		(loadZones || loadCountryZones)(country).then((list) => {
			if (current) {
				setZones(list);
				if (onZones) {
					onZones(list);
				}
			}
		});
		return () => { current = false; };
	}, [country]);

	const decided = zones === null ? null : decideZone(zones, value, latitude, longitude);
	const zone = decided ? decided.zone : null;

	useEffect(() => {
		if (decided && (zone || null) !== (value || null)) {
			onChange(zone);
		}
		// Only when the worked-out zone changes (not on every new onChange function)
	}, [zone, decided && decided.source]);

	const text = (content) => <div className="location-text-input" style={{ border: 'none', paddingLeft: 0 }}>{content}</div>;

	if (row) {
		if (!country || decided === null || decided.source !== 'choice') {
			return null;
		}
		return (
			<div className="row company-text-area">
				<label htmlFor={rowId} className="col-md-3 control-label">Time zone</label>
				<div className="col-md-9">
					<select id={rowId} className="location-text-input" value={zone || ''} onChange={(event) => onChange(event.target.value || null)} required>
						<option value="">Choose the time zone</option>
						{zones.map((name) => <option key={name} value={name}>{name.replace(/_/g, ' ')}</option>)}
					</select>
				</div>
			</div>
		);
	}

	if (onlyWhenChoice) {
		if (!country || decided === null || decided.source !== 'choice') {
			return null;
		}
		return (
			<div className="form-group">
				<label htmlFor="time-zone-choice">Time zone</label>
				<select id="time-zone-choice" className="location-text-input" value={zone || ''} onChange={(event) => onChange(event.target.value || null)} required>
					<option value="">Choose your time zone</option>
					{zones.map((name) => <option key={name} value={name}>{name.replace(/_/g, ' ')}</option>)}
				</select>
			</div>
		);
	}

	if (!country) {
		return text('Choose the country first: the time zone comes from it');
	}
	if (decided === null) {
		return text('Working out the time zone…');
	}
	if (decided.source === 'none') {
		return text('No time zone is known for this country');
	}
	if (decided.source === 'position') {
		return text('Time zone: ' + zone + ' (from the map position)');
	}
	const positionHint = decided.positionZone && zones.indexOf(decided.positionZone) === -1
		? <div style={{ color: '#856404', fontSize: '0.9em' }}>The map position is in {decided.positionZone}, which is not in this country: check the country.</div>
		: null;
	if (decided.source === 'country') {
		return <div>{text('Time zone: ' + zone + ' (from country)')}{positionHint}</div>;
	}
	return (
		<div>
			<select className="location-text-input" value={zone || ''} onChange={(event) => onChange(event.target.value || null)} required>
				<option value="">Choose the time zone…</option>
				{zones.map((name) => <option key={name} value={name}>{name.replace(/_/g, ' ')}</option>)}
			</select>
			{positionHint}
		</div>
	);
}
